import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(duration);
dayjs.extend(relativeTime);

/**
 * Format seconds as "m:ss" (e.g., "3:45") or "h:mm:ss" for durations >= 1 hour.
 */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const dur = dayjs.duration(seconds, "seconds");
  if (dur.asHours() >= 1) {
    const hours = Math.floor(dur.asHours());
    const mins = dur.minutes();
    const secs = dur.seconds();
    return `${hours}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  }
  return dur.format("m:ss");
}

/**
 * Format a date as "MMM D, YYYY" (e.g., "Dec 13, 2025")
 * Accepts Date objects or ISO date strings (for JSON-serialized responses).
 */
export function formatDate(date: Date | string): string {
  return dayjs(date).format("MMM D, YYYY");
}

/**
 * Format a date as relative time (e.g., "2 hours ago", "3 days ago")
 * Accepts Date objects or ISO date strings (for JSON-serialized responses).
 */
export function formatRelativeTime(date: Date | string): string {
  return dayjs(date).fromNow();
}

/**
 * Parse a timestamp string like "1:30" or "1:30:45" into seconds.
 * Returns null if the format is invalid.
 */
export function parseTimestamp(timestamp: string): number | null {
  const parts = timestamp.split(":").map((p) => parseInt(p, 10));
  if (parts.some((p) => Number.isNaN(p) || p < 0)) return null;

  if (parts.length === 2) {
    // m:ss format
    const minutes = parts[0];
    const seconds = parts[1];
    if (minutes === undefined || seconds === undefined) return null;
    if (seconds >= 60) return null;
    return minutes * 60 + seconds;
  }
  if (parts.length === 3) {
    // h:mm:ss format
    const hours = parts[0];
    const minutes = parts[1];
    const seconds = parts[2];
    if (hours === undefined || minutes === undefined || seconds === undefined)
      return null;
    if (minutes >= 60 || seconds >= 60) return null;
    return hours * 3600 + minutes * 60 + seconds;
  }

  return null;
}
